import React from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Tooltip,
  Collapse,
  Typography,
} from '@mui/material';
import {
  Folder as FolderIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore,
  ChevronRight,
} from '@mui/icons-material';
import { LibraryFolder } from '../../types/library';

interface FolderTreeViewProps {
  folders: LibraryFolder[];
  selectedFolderId: number | null;
  onSelectFolder: (folder: LibraryFolder) => void;
  onCreateFolder?: (parentId?: number) => void;
  onEditFolder?: (folder: LibraryFolder) => void;
  onDeleteFolder?: (folder: LibraryFolder) => void;
  isAdmin: boolean;
}

const FolderTreeView: React.FC<FolderTreeViewProps> = ({
  folders,
  selectedFolderId,
  onSelectFolder,
  onCreateFolder,
  onEditFolder,
  onDeleteFolder,
  isAdmin,
}) => {
  const [expandedIds, setExpandedIds] = React.useState<Set<number>>(new Set());

  const toggleExpand = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const renderFolder = (folder: LibraryFolder, level: number = 0) => {
    const hasChildren = folder.children && folder.children.length > 0;
    const isExpanded = expandedIds.has(folder.id);
    const isSelected = selectedFolderId === folder.id;

    return (
      <React.Fragment key={folder.id}>
        <ListItemButton
          selected={isSelected}
          onClick={() => onSelectFolder(folder)}
          sx={{
            pl: `${8 + level * 16}px`,
            pr: 1,
            py: 0.5,
            borderRadius: 1,
            mb: 0.5,
            alignItems: 'center',
            '&.Mui-selected': {
              backgroundColor: 'primary.light',
              color: 'primary.dark',
              '&:hover': { backgroundColor: '#c2d6a3' },
            },
          }}
        >
          {/* Single fixed-width glyph slot keeps every same-level row aligned.
              Parents show the expand arrow (indication enough); leaf folders show the folder icon. */}
          <Box
            sx={{
              width: 24,
              height: 24,
              mr: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color: 'inherit',
            }}
          >
            {hasChildren ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpand(folder.id);
                }}
                sx={{ p: 0.25, color: 'inherit' }}
              >
                {isExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
              </IconButton>
            ) : (
              <FolderIcon fontSize="small" />
            )}
          </Box>
          <ListItemText
            primary={folder.name}
            sx={{ m: 0, minWidth: 0 }}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: isSelected ? 600 : 400,
              noWrap: true,
            }}
          />
          {isAdmin && (
            <Box
              sx={{
                display: 'flex',
                gap: 0.25,
                ml: 0.5,
                flexShrink: 0,
                color: 'inherit',
                opacity: 0.45,
                '.MuiListItemButton-root:hover &': { opacity: 1 },
                transition: 'opacity 0.2s',
              }}
            >
              <Tooltip title="Add subfolder">
                <IconButton
                  size="small"
                  sx={{ p: 0.5, color: 'inherit' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCreateFolder?.(folder.id);
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  sx={{ p: 0.5, color: 'inherit' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditFolder?.(folder);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  sx={{ p: 0.5, color: 'inherit' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteFolder?.(folder);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </ListItemButton>
        {hasChildren && (
          <Collapse in={isExpanded}>
            {folder.children!.map((child) => renderFolder(child, level + 1))}
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Folders
        </Typography>
        {isAdmin && onCreateFolder && (
          <Tooltip title="New root folder">
            <IconButton size="small" onClick={() => onCreateFolder()}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <List dense sx={{ px: 0.5 }}>
        {folders.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
            No folders yet
          </Typography>
        ) : (
          folders.map((folder) => renderFolder(folder))
        )}
      </List>
    </Box>
  );
};

export default FolderTreeView;
